import { isWeb, ScrollView, XStack, YStack, YStackProps } from '@mythical/ui'
import { ButtonLink, ButtonProps } from 'app/components/primitive/Button'
import {
  IconBookmark,
  IconChat,
  IconGift,
  IconCollectibles,
  IconHeart,
  IconHome,
  IconPlaylist,
  IconUser,
} from 'app/components/primitive/Icon'
import Version from 'app/components/primitive/Version/index'
import { useCurrentPath } from 'app/hooks/use-current-path'
import getEnv from 'app/lib/env-getter'
import { useStores } from 'app/store'
import { pageLinks, SIDEBAR_WIDTH } from './constants'
import { memo } from 'react'

type SidebarLink = {
  label: string
  href: string
  icon: any
  isLoggedIn?: boolean
  match?: string[]
}

const primaryLinks: SidebarLink[] = [
  {
    label: 'Home',
    icon: IconHome,
    href: isWeb ? '/' : '/homescreen',
    match: ['/', '/homescreen'],
  },
  {
    label: 'Videos',
    href: '/content',
    icon: IconPlaylist,
  },
  {
    label: 'Merch & Collectibles',
    href: '/merch',
    icon: IconGift,
  },
  {
    label: 'My Collectibles',
    href: '/my-collectibles',
    icon: IconCollectibles,
    isLoggedIn: true,
  },
]

const secondaryLinks: SidebarLink[] = [
  {
    label: 'Watch Later',
    href: '/watch-later',
    icon: IconBookmark,
    isLoggedIn: true,
  },
  {
    label: 'Favorites',
    href: '/favorites',
    icon: IconHeart,
    isLoggedIn: true,
  },
  {
    label: 'Comments',
    href: '/user/comments',
    icon: IconChat,
    isLoggedIn: true,
  },
]

type SidebarProps = {} & YStackProps

const Sidebar = memo(({}: SidebarProps) => {
  const { path } = useCurrentPath()
  const { userStore } = useStores()
  const isLoggedIn = !!userStore?.user?.id
  const canMemberfulLink = userStore?.canMemberfulLink ?? false

  return (
    <YStack
      bg="$bgPrimary"
      w={SIDEBAR_WIDTH}
      display="none"
      $gtMd={{ display: 'flex' }}
    >
      <ScrollView
        pl="$5"
        pr="$2.5"
        pt="$6"
        showsVerticalScrollIndicator={isWeb}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <YStack flexGrow={1} gap="$6" pb="$4" w="100%" h="100%" mx="auto">
          <YStack gap="$1.5">
            {primaryLinks
              .filter((link) => !link.isLoggedIn || isLoggedIn)
              .map((link) => (
                <SidebarButton
                  key={link.label}
                  link={link}
                  isActive={
                    path === link.href || (link.match?.includes(path) ?? false)
                  }
                />
              ))}
          </YStack>
          <YStack gap="$1.5">
            {secondaryLinks
              .filter((link) => !link.isLoggedIn || isLoggedIn)
              .map((link) => (
                <SidebarButton
                  key={link.label}
                  link={link}
                  isActive={
                    path === link.href || (link.match?.includes(path) ?? false)
                  }
                />
              ))}
          </YStack>

          {getEnv() !== 'production' && (
            <SidebarButton
              link={{
                label: 'Mock Login',
                href: '/mock-login',
                icon: IconUser,
              }}
              isActive={path === '/mock-login'}
            />
          )}

          <YStack mt="auto" gap="$3.5">
            <XStack mx="auto" flexWrap="wrap" jc="center" gap="$2">
              {pageLinks
                .filter((link) =>
                  link.href === '/perks' ? canMemberfulLink : true,
                )
                .map((link) => (
                  <ButtonLink
                    key={link.label}
                    href={link.href}
                    size="$2"
                    fontSize="$2"
                    theme="secondary"
                  >
                    {link.label}
                  </ButtonLink>
                ))}
            </XStack>
            <Version />
          </YStack>
        </YStack>
      </ScrollView>
    </YStack>
  )
})

export default Sidebar

type SidebarButtonProps = {
  link: SidebarLink
  isActive: boolean
  external?: boolean
} & ButtonProps

const SidebarButton = ({
  link,
  isActive,
  external,
  ...props
}: SidebarButtonProps) => (
  <ButtonLink
    theme={isActive ? 'bgSecondary' : 'bgPrimary'}
    color={isActive ? '$fgPrimary' : '$fgSecondary2'}
    href={link.href}
    size="$4"
    fontFamily="$title"
    fontSize="$m"
    px="$3"
    br="$5"
    jc="flex-start"
    h={50}
    bw={0}
    icon={<link.icon size={24} />}
    {...(external && { target: '_blank' })}
    {...props}
  >
    {link.label}
  </ButtonLink>
)
